import * as React from "react";
import { Typography } from "@material-ui/core";
import DarkDataGrid from "common/components/DarkDataGrid/DarkDataGrid";
import { SetLoading } from "common/components/Loader/Loader";
import { toast } from "react-toastify";
import EventService from "services/EventService";
import { useEventManagerStyles } from "./eventManagerStyles";
import { utc } from "./utcDate";

const columns = [
	{ field: "stage", headerName: "Stage", flex: 2.2, minWidth: 125 },
	{ field: "stageType", headerName: "Type", flex: 1.1, minWidth: 70 },
	{ field: "startTime", headerName: "Start", flex: 1.7, minWidth: 100 },
	{ field: "decided", headerName: "Decided", flex: 1.9, minWidth: 115 },
	{ field: "slots", headerName: "Slots", flex: 1, minWidth: 60 },
	{ field: "teams", headerName: "Teams", flex: 1, minWidth: 60 },
	{ field: "gate", headerName: "Needs", flex: 1, minWidth: 60 },
];

const decidedProgress = (stage) => {
	if (stage.bracketPicks) {
		return `${stage.decidedPicks}/${stage.bracketPicks} matches`;
	}
	return stage.groupPicks
		? `${stage.advancingPicks}/${stage.groupPicks} advancing`
		: "-";
};

function Facts({ rows, classes }) {
	return (
		<div className={classes.facts}>
			{rows.map(([label, value]) => (
				<React.Fragment key={label}>
					<Typography variant="body2" className={classes.label}>
						{label}
					</Typography>
					<Typography variant="body2">{value}</Typography>
				</React.Fragment>
			))}
		</div>
	);
}

export default function DiagnosticsTab({ eventId }) {
	const classes = useEventManagerStyles();
	const [data, setData] = React.useState(null);

	const load = React.useCallback(async () => {
		try {
			SetLoading(true, "Loading Status");
			setData(await EventService.getDiagnostics(eventId));
		} catch (error) {
			toast(error?.message?.trim() || "Failed to load event status.", {
				type: "error",
			});
		} finally {
			SetLoading(false);
		}
	}, [eventId]);

	React.useEffect(() => {
		load();
	}, [load]);

	if (!data) {
		return null;
	}

	const advance = data.advance ?? {};
	const stages = data.stages ?? [];
	const current = stages.find((s) => s.stageIndex === data.currentStage);

	const rows = stages.map((s) => ({
		id: s.stageIndex,
		stage: `${s.stageIndex}. ${s.name ?? "(unnamed)"}`,
		stageType: s.stageType ?? "-",
		startTime: utc(s.startTime),
		decided: decidedProgress(s),
		slots: s.bracketPicks ? s.filledSlots : "-",
		teams: s.teams,
		gate: s.teamsToOpenPicks ?? "-",
	}));

	const picksProtected = stages
		.filter((s) => s.lockScoring)
		.map((s) => s.stageIndex);

	const picks = data.picksOpen
		? `open, locks ${utc(data.lockDate)} UTC`
		: `locked${data.lockDate ? ` since ${utc(data.lockDate)} UTC` : ""}`;

	const automation = data.automation ?? {};

	return (
		<>
			<Facts
				classes={classes}
				rows={[
					[
						"Current stage",
						current
							? `${current.stageIndex} - ${current.name ?? "(unnamed)"}`
							: `${data.currentStage ?? "-"}`,
					],
					["Picks", picks],
					[
						"Event window",
						`${utc(data.eventStartDate)} to ${utc(data.eventEndDate)} UTC`,
					],
					...(picksProtected.length
						? [
								[
									"Picks kept from re-sync",
									`stage ${picksProtected.join(", ")} (LockScoring)`,
								],
						  ]
						: []),
					...(data.announcedParticipants == null
						? []
						: [
								[
									"Announced participants",
									`${data.announcedParticipants} team(s) offered by Going All The Way`,
								],
						  ]),
				]}
			/>

			<Typography variant="subtitle2" className={classes.section}>
				Advance to next stage
			</Typography>
			<Facts
				classes={classes}
				rows={[
					["Next stage", advance.nextStageIndex ?? "-"],
					["Stopped at", advance.blocker ?? "-"],
					["Reading", advance.detail ?? "-"],
					[
						"Teams counted",
						advance.teamsRequired == null
							? "not reached"
							: `${advance.teamsCounted} of ${advance.teamsRequired}`,
					],
					[
						"Next stage start",
						advance.nextStageStartTime
							? `${utc(advance.nextStageStartTime)} UTC`
							: "not set",
					],
				]}
			/>

			<Typography variant="subtitle2" className={classes.section}>
				Stages (times UTC)
			</Typography>
			<DarkDataGrid
				className={classes.grid}
				rows={rows}
				columns={columns}
				autoHeight
				hideFooter
				disableSelectionOnClick
				disableColumnMenu
			/>

			<Typography variant="subtitle2" className={classes.section}>
				Sync
			</Typography>
			<Facts
				classes={classes}
				rows={[
					["Last fetch", `${utc(data.lastFetchedAt)} UTC`],
					["Flagged for resync", data.liquipediaNotifiedChange ? "yes" : "no"],
					[
						"Last stored payload",
						data.payloadFetchedAt
							? `${utc(data.payloadFetchedAt)} UTC - ${data.payloadWiki} / ${
									data.payloadPage
							  }`
							: "none stored",
					],
					[
						"Automation errors (3h)",
						automation.errorsLast3h > 0
							? `${automation.errorsLast3h} - last ${utc(
									automation.lastErrorAt
							  )} UTC: ${automation.lastErrorMessage}`
							: "0",
					],
					[
						"Last rate limit",
						automation.lastRateLimitAt
							? `${utc(automation.lastRateLimitAt)} UTC`
							: "none recorded",
					],
				]}
			/>
		</>
	);
}
